import { Copy, Download, FileText, Image as ImageIcon, Trash2 } from "lucide-react";
import { copyFile } from "../lib/fileActions";
import { formatBytes } from "../lib/format";
import type { PublicPadFile } from "../shared";
import { Button } from "./ui/Button";

export function FileRow({
  file,
  onDelete,
  onNotice,
}: {
  file: PublicPadFile;
  onDelete: (fileId: string) => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const isImage = file.type.startsWith("image/");

  return (
    <article className="grid min-h-[72px] grid-cols-[52px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-neutral-200 bg-white p-2 shadow-[0_1px_1px_rgba(0,0,0,0.03)] transition hover:border-neutral-300 hover:bg-neutral-50/70">
      <a
        className="flex h-13 w-13 items-center justify-center overflow-hidden rounded-md border border-neutral-200 bg-neutral-50 text-neutral-500 outline-none transition hover:border-neutral-300 focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
        href={file.downloadUrl}
        target="_blank"
        rel="noreferrer"
        title="Open file"
      >
        {isImage ? <img className="h-full w-full object-cover" src={file.downloadUrl} alt="" loading="lazy" /> : <FileText size={25} />}
      </a>

      <div className="min-w-0">
        <a className="block truncate text-sm font-medium text-neutral-950 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2" href={file.downloadUrl} target="_blank" rel="noreferrer">
          {file.name}
        </a>
        <span className="mt-1 block truncate text-xs text-neutral-500">
          {formatBytes(file.size)} / {file.type || "file"}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          title={isImage ? "Copy image" : "Copy file content or link"}
          onClick={() =>
            void copyFile(file)
              .then(onNotice)
              .catch((error: unknown) => onNotice(error instanceof Error ? error.message : "Could not copy file"))
          }
        >
          {isImage ? <ImageIcon size={17} /> : <Copy size={17} />}
        </Button>
        <a
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-neutral-600 outline-none transition hover:bg-neutral-100 hover:text-neutral-950 focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
          href={file.downloadUrl}
          download={file.name}
          title="Download"
        >
          <Download size={17} />
        </a>
        <Button
          size="icon"
          variant="ghost"
          className="text-red-700 hover:bg-red-50 hover:text-red-800"
          title="Delete"
          onClick={() =>
            void onDelete(file.id)
              .then(() => onNotice("File deleted"))
              .catch(() => onNotice("Could not delete file"))
          }
        >
          <Trash2 size={17} />
        </Button>
      </div>
    </article>
  );
}
